<template>
  <div
    :data-type="context.type"
    :class="context.classes.element"
  >
    <VueMultiselect
      :id="context.id"
      :value="multiselectValue"
      :options="searchResults"
      :multiple="multiple"
      :max="multiple && selectionLimit ? selectionLimit : false"
      :track-by="dataKey"
      :label="labelField"
      :custom-label="getOptionLabel"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :loading="loading"
      :searchable="true"
      :internal-search="false"
      :clear-on-select="!multiple"
      :close-on-select="!multiple"
      :preserve-search="false"
      :hide-selected="multiple"
      :allow-empty="true"
      :show-labels="false"
      :max-height="320"
      class="lazy-autocomplete"
      :class="{ 'lazy-autocomplete--disabled': isDisabled, 'lazy-autocomplete--clearable': showClear }"
      @input="onSelect"
      @search-change="onSearchChange"
      @close="onClose"
    >
      <template #option="{ option }">
        <div class="lazy-autocomplete__option">
          <div class="lazy-autocomplete__option-label">{{ getOptionLabel(option) }}</div>
          <div
            v-if="getOptionSublabel(option)"
            class="lazy-autocomplete__option-sublabel"
          >
            {{ getOptionSublabel(option) }}
          </div>
        </div>
      </template>
      <template #clear>
        <!-- vue-multiselect has no styles for its own .multiselect__clear, so this is our own button (left of the caret) -->
        <button
          v-if="showClear"
          type="button"
          class="lazy-autocomplete__clear"
          :title="$t('formulateLazyAutocomplete.clear')"
          :aria-label="$t('formulateLazyAutocomplete.clear')"
          @mousedown.prevent.stop
          @click.prevent.stop="clearSelection"
        >
          <span class="pi pi-times"></span>
        </button>
      </template>
      <template #noOptions>
        <span class="text-muted">{{ $tc('formulateLazyAutocomplete.typeToSearch', minChars, { count: minChars }) }}</span>
      </template>
      <template #noResult="{ search }">
        <span
          v-if="normalizeQuery(search).length < minChars"
          class="text-muted"
        >
          {{ $tc('formulateLazyAutocomplete.typeToSearch', minChars, { count: minChars }) }}
        </span>
        <span
          v-else-if="searchFailed"
          class="text-danger"
        >
          {{ $t('formulateLazyAutocomplete.searchError') }}
        </span>
        <span
          v-else
          class="text-muted"
        >
          {{ $t('formulateLazyAutocomplete.noMatches', { search: normalizeQuery(search) }) }}
        </span>
      </template>
      <template #maxElements>
        {{ $t('formulateLazyAutocomplete.maxElements', { max: selectionLimit }) }}
      </template>
      <template #beforeList>
        <!-- while a search runs there are no options (the old matches are cleared), so say so instead of showing an empty list -->
        <div
          v-if="loading && !searchResults.length"
          class="lazy-autocomplete__searching"
        >
          {{ $t('formulateLazyAutocomplete.searching') }}
        </div>
      </template>
      <template #afterList>
        <div
          v-if="hasMoreResults && !loading"
          class="lazy-autocomplete__more"
        >
          {{ $t('formulateLazyAutocomplete.moreResults', { shown: searchResults.length, total: totalCount }) }}
        </div>
      </template>
    </VueMultiselect>
  </div>
</template>

<script>
  import VueMultiselect from 'vue-multiselect';
  import debounce from 'lodash/debounce';
  import get from 'lodash/get';
  import invoke from 'lodash/invoke';
  import { splAxiosBare, suppliosApi } from '@/js/api-service';

  // vue-formulate custom input ('lazy_autocomplete') -- pick one (or several) items from a list that is too big to load up front
  // nothing is fetched until the user types at least minChars characters, then we search the API (debounced) and show only the matches
  // reads/writes context.model:
  //    - with valueField set (the normal case, ex: flow forms):  the value of that key (an ID), or an array of them if multiple
  //    - without valueField:  the whole option object, or an array of them if multiple
  // the options passed in (context.options) are NOT the full list -- only the currently-selected option(s), so we can show their labels without a fetch
  // see FormulateLazyAutocomplete in state_machines/flows/forms/formulate_fields.py for the flow-form side
  export default {
    name: 'FormulateLazyAutocomplete',
    components: {
      VueMultiselect,
    },
    i18n: {
      messages: {
        en: {
          formulateLazyAutocomplete: {
            placeholder: 'Type to search...',
            searching: 'Searching...',
            typeToSearch: 'Type at least {count} character to search | Type at least {count} characters to search',
            noMatches: 'No matches for "{search}"',
            searchError: 'Search failed. Please try again.',
            moreResults: 'Showing the first {shown} of {total} matches. Keep typing to narrow the results.',
            maxElements: 'Maximum of {max} selections. Remove one to select another.',
            clear: 'Clear selection',
          },
        },
        es: {
          formulateLazyAutocomplete: {
            placeholder: 'Escriba para buscar...',
            searching: 'Buscando...',
            typeToSearch: 'Escriba al menos {count} carácter para buscar | Escriba al menos {count} caracteres para buscar',
            noMatches: 'No hay coincidencias para "{search}"',
            searchError: 'La búsqueda falló. Inténtelo de nuevo.',
            moreResults: 'Mostrando las primeras {shown} de {total} coincidencias. Siga escribiendo para acotar los resultados.',
            maxElements: 'Máximo de {max} selecciones. Quite una para seleccionar otra.',
            clear: 'Borrar selección',
          },
        },
        pt: {
          formulateLazyAutocomplete: {
            placeholder: 'Digite para pesquisar...',
            searching: 'Pesquisando...',
            typeToSearch: 'Digite pelo menos {count} caractere para pesquisar | Digite pelo menos {count} caracteres para pesquisar',
            noMatches: 'Nenhum resultado para "{search}"',
            searchError: 'A pesquisa falhou. Tente novamente.',
            moreResults: 'Mostrando os primeiros {shown} de {total} resultados. Continue digitando para refinar a busca.',
            maxElements: 'Máximo de {max} seleções. Remova uma para selecionar outra.',
            clear: 'Limpar seleção',
          },
        },
        'zh-Hans': {
          formulateLazyAutocomplete: {
            placeholder: '输入以搜索...',
            searching: '正在搜索...',
            typeToSearch: '请至少输入 {count} 个字符进行搜索',
            noMatches: '未找到与“{search}”匹配的结果',
            searchError: '搜索失败，请重试。',
            moreResults: '显示前 {shown} 条结果（共 {total} 条）。继续输入以缩小范围。',
            maxElements: '最多可选择 {max} 项。请先移除一项再选择其他项。',
            clear: '清除选择',
          },
        },
        'zh-Hant': {
          formulateLazyAutocomplete: {
            placeholder: '輸入以搜尋...',
            searching: '搜尋中...',
            typeToSearch: '請至少輸入 {count} 個字元進行搜尋',
            noMatches: '找不到與「{search}」相符的結果',
            searchError: '搜尋失敗，請再試一次。',
            moreResults: '顯示前 {shown} 筆結果（共 {total} 筆）。繼續輸入以縮小範圍。',
            maxElements: '最多可選取 {max} 項。請先移除一項再選取其他項目。',
            clear: '清除選取',
          },
        },
      },
    },
    props: {
      context: {
        // the required Formulate context prop
        type: Object,
        required: true,
      },
      multiple: {
        // if true, the value is always an array
        type: Boolean,
        default: false,
      },
      selectionLimit: {
        // OPTIONAL max number of selections - only used if multiple=true
        type: Number,
        required: false,
      },
      apiEndpoint: {
        // dotted name of the suppliosApi endpoint to search with, without the suppliosApi part (ex: 'item.list'), or the endpoint function itself
        // it is called as endpoint(...apiEndpointArgs, axiosConfig) -- the search goes in axiosConfig.params
        // wins over 'url' if both are set
        type: [String, Function],
        required: false,
      },
      apiEndpointArgs: {
        // any args the apiEndpoint needs BEFORE the axios config (ex: a parent ID)
        type: Array,
        required: false,
      },
      url: {
        // instead of apiEndpoint, a URL to GET (absolute or relative) -- the search goes in the query params
        type: String,
        required: false,
      },
      resultsKey: {
        // where the array of results is in the response data (ex: 'results' for paginated endpoints)
        // if not set, we use the response data if it is an array, or data.results if that is an array
        type: String,
        required: false,
      },
      searchParam: {
        // the query param name for the search text (DRF SearchFilter uses 'search')
        type: String,
        default: 'search',
      },
      extraParams: {
        // extra query params sent with every search (ex: { ordering: 'part_number' } or filters the endpoint supports)
        type: Object,
        required: false,
      },
      pageSizeParam: {
        // the query param name for the page size -- set to empty string if the endpoint does not paginate
        type: String,
        default: 'page_size',
      },
      pageSize: {
        // how many matches to fetch per search
        type: Number,
        default: 25,
      },
      minChars: {
        // how many characters must be typed before we search
        type: Number,
        default: 2,
      },
      debounceMs: {
        // wait this long after the last keystroke before searching
        type: Number,
        default: 300,
      },
      labelField: {
        // the key in each option object to show as its label
        type: String,
        default: 'label',
      },
      sublabelField: {
        // OPTIONAL key in each option object to show as a second, muted line in the dropdown (ex: 'description')
        type: String,
        required: false,
      },
      valueField: {
        // OPTIONAL - if set, we store only this key of the selected option(s) in the model (ex: 'id') -- if not set, we store the whole object(s)
        type: String,
        required: false,
      },
      dataKey: {
        // unique key in each option object, used to track selections (should usually be the same as valueField)
        type: String,
        default: 'id',
      },
      disabledField: {
        // key in each option object that means 'cannot be selected'
        type: String,
        default: 'disabled',
      },
      options: {
        // usually empty -- with classification 'select' the passed options are in context.options instead (we check both)
        // these are ONLY the currently-selected option(s), so we can show their labels without fetching
        type: Array,
        required: false,
      },
    },
    data() {
      return {
        selectedObjects: [], // always an array, even for single select -- what we pass to the multiselect (via multiselectValue)
        searchResults: [], // the matches for the current search text
        totalCount: null, // total matches from a paginated response (data.count), if we have it
        loading: false,
        searchFailed: false,
        latestRequestId: 0, // to drop responses that come back after a newer search started
        knownOptions: {}, // every option object we have seen, keyed by String(value) -- so we can turn model values back into objects
      };
    },
    computed: {
      passedOptions() {
        const rawOptions = this.options || this.context.options;
        return Array.isArray(rawOptions) ? rawOptions : [];
      },
      multiselectValue() {
        return this.multiple ? this.selectedObjects : (this.selectedObjects[0] ?? null);
      },
      isDisabled() {
        // flow forms send disabled: true, but a bare `disabled` attribute in a template arrives as '' (empty string)
        const disabled = this.context.attributes?.disabled;
        return disabled === '' || (!!disabled && disabled !== 'false');
      },
      placeholder() {
        return this.context.attributes?.placeholder || this.context.placeholder || this.$t('formulateLazyAutocomplete.placeholder');
      },
      showClear() {
        // multiple select already has an X on each tag
        return !this.multiple && !this.isDisabled && this.selectedObjects.length > 0;
      },
      hasMoreResults() {
        const isMaxedOut = this.multiple && !!this.selectionLimit && this.selectedObjects.length >= this.selectionLimit; // the list is replaced by the max message then
        return !isMaxedOut && typeof this.totalCount === 'number' && this.totalCount > this.searchResults.length;
      },
    },
    watch: {
      passedOptions: {
        handler(newOptions) {
          newOptions.forEach((opt) => this.rememberOption(opt));
          this.syncSelectionFromModel(this.context.model); // labels may have just become available
        },
        immediate: true,
      },
      'context.model': {
        handler: 'syncSelectionFromModel',
        immediate: true,
      },
    },
    created() {
      this.debouncedSearch = debounce(this.runSearch, this.debounceMs);
    },
    beforeDestroy() {
      this.debouncedSearch.cancel();
    },
    methods: {
      normalizeQuery(query) {
        return (query || '').trim();
      },
      getOptionLabel(option) {
        if (!option) {
          return '';
        }
        const label = get(option, this.labelField);
        return label || label === 0 ? String(label) : String(this.getOptionKey(option) ?? '');
      },
      getOptionSublabel(option) {
        return this.sublabelField ? get(option, this.sublabelField) || '' : '';
      },
      getOptionKey(option) {
        // the key we use to recognize an option -- the stored value if we store values, otherwise the dataKey
        return get(option, this.valueField || this.dataKey);
      },
      getModelValue(option) {
        // what we store in context.model for a selected option
        if (this.valueField) {
          return get(option, this.valueField);
        }
        const { $isDisabled, ...cleanOption } = option; // eslint-disable-line no-unused-vars
        return cleanOption;
      },
      rememberOption(option) {
        const key = option ? this.getOptionKey(option) : undefined;
        if (key !== undefined && key !== null && key !== '') {
          this.knownOptions[String(key)] = option;
        }
      },
      makeOption(rawOption) {
        // shallow copy, plus the flag vue-multiselect uses for disabled options
        return { ...rawOption, $isDisabled: !!get(rawOption, this.disabledField) };
      },
      syncSelectionFromModel(value) {
        // turn context.model into the selected option objects -- runs when the model changes from outside OR from our own onSelect()
        let values;
        if (Array.isArray(value)) {
          values = value;
        } else {
          values = value || value === 0 ? [value] : [];
        }
        values = values.filter((v) => v || v === 0);
        if (!this.multiple) {
          values = values.slice(0, 1);
        }
        if (!this.valueField) {
          // the model already holds the full objects
          this.selectedObjects = values.filter((v) => typeof v === 'object');
          return;
        }
        this.selectedObjects = values.map((v) => {
          const known = this.knownOptions[String(v)];
          if (known) {
            return known;
          }
          // no label for this value yet (it was not in the passed options) -- keep the value, show it as-is rather than drop it
          const placeholderOption = { [this.valueField]: v, [this.labelField]: String(v) };
          if (this.dataKey !== this.valueField) {
            placeholderOption[this.dataKey] = v;
          }
          return placeholderOption;
        });
      },
      onSelect(selection) {
        // from vue-multiselect: one option object (or null when cleared) for single, an array of option objects for multiple
        const selectedList = Array.isArray(selection) ? selection : selection ? [selection] : [];
        selectedList.forEach((opt) => this.rememberOption(opt));
        this.selectedObjects = selectedList;
        if (this.multiple) {
          this.context.model = selectedList.map((opt) => this.getModelValue(opt));
        } else {
          this.context.model = selectedList.length ? this.getModelValue(selectedList[0]) : null;
        }
      },
      clearSelection() {
        this.onSelect(this.multiple ? [] : null);
        this.context.blurHandler(); // so flow forms save the cleared value, same as after picking one
      },
      onClose() {
        this.context.blurHandler(); // triggers validation + the flow-form background save
      },
      resetSearch() {
        this.debouncedSearch.cancel();
        this.latestRequestId += 1; // any request still in flight is now stale
        this.searchResults = [];
        this.totalCount = null;
        this.loading = false;
        this.searchFailed = false;
      },
      onSearchChange(query) {
        const cleanQuery = this.normalizeQuery(query);
        if (cleanQuery.length < this.minChars) {
          // also runs with '' when the dropdown closes -- clears the old matches
          this.resetSearch();
          return;
        }
        // invalidate any request still in flight NOW, not when the debounced search fires -- otherwise its response can land
        // during the debounce and show matches for the old text under the new text
        this.latestRequestId += 1;
        // and drop the old matches: left in place they stay clickable (and Enter picks the highlighted one) under the new text
        this.searchResults = [];
        this.totalCount = null;
        this.loading = true; // show the spinner (and 'Searching...') right away, even while debouncing
        this.searchFailed = false;
        this.debouncedSearch(cleanQuery);
      },
      buildRequestConfig(query) {
        const params = { ...(this.extraParams || {}), [this.searchParam]: query };
        if (this.pageSizeParam && this.pageSize) {
          params[this.pageSizeParam] = this.pageSize;
        }
        return { params };
      },
      fetchMatches(query) {
        const config = this.buildRequestConfig(query);
        const endpointArgs = this.apiEndpointArgs || [];
        if (typeof this.apiEndpoint === 'function') {
          return this.apiEndpoint(...endpointArgs, config);
        }
        if (this.apiEndpoint) {
          return invoke(suppliosApi, this.apiEndpoint, ...endpointArgs, config);
        }
        if (this.url) {
          return splAxiosBare.get(this.url, config);
        }
        return Promise.reject(new Error(`FormulateLazyAutocomplete [${this.context.name}] needs an apiEndpoint or url to search`));
      },
      extractResults(data) {
        if (this.resultsKey) {
          return get(data, this.resultsKey) || [];
        }
        if (Array.isArray(data)) {
          return data;
        }
        return Array.isArray(data?.results) ? data.results : [];
      },
      async runSearch(query) {
        const requestId = ++this.latestRequestId;
        try {
          const response = await this.fetchMatches(query);
          if (requestId !== this.latestRequestId) {
            return; // a newer search started (or the search was cleared) while this one was running
          }
          const data = response?.data;
          const results = this.extractResults(data).map((raw) => this.makeOption(raw));
          results.forEach((opt) => this.rememberOption(opt));
          this.searchResults = results;
          this.totalCount = typeof data?.count === 'number' ? data.count : null;
        } catch (error) {
          if (requestId !== this.latestRequestId) {
            return;
          }
          console.error(`FormulateLazyAutocomplete [${this.context.name}] search failed`, error);
          this.searchResults = [];
          this.totalCount = null;
          this.searchFailed = true;
        } finally {
          if (requestId === this.latestRequestId) {
            this.loading = false;
          }
        }
      },
    },
  };
</script>

<style lang="scss">
  .lazy-autocomplete {
    &.multiselect--disabled.lazy-autocomplete--disabled {
      opacity: 1; // vue-multiselect fades disabled inputs to 0.6, but disabled is how we show read-only values
      .multiselect__tags,
      .multiselect__single,
      .multiselect__select {
        background-color: var(--gray-200);
      }
    }

    &.lazy-autocomplete--clearable .multiselect__tags {
      padding-right: 64px; // room for the clear button AND the caret (default is 40px for the caret only)
    }

    .lazy-autocomplete__clear {
      position: absolute;
      top: 50%;
      right: 40px; // the caret (.multiselect__select) is 40px wide on the right
      z-index: 2;
      transform: translateY(-50%);
      padding: 0.25rem;
      line-height: 1;
      font-size: 0.75rem;
      color: var(--gray-600);
      background: transparent;
      border: 0;
      border-radius: $border-radius-sm;
      cursor: pointer;

      &:hover,
      &:focus {
        color: var(--gray-900);
        background-color: var(--gray-200);
      }
    }

    &.multiselect--active .lazy-autocomplete__clear {
      display: none; // the search input takes over while the dropdown is open
    }

    .lazy-autocomplete__option {
      white-space: normal;
    }

    .lazy-autocomplete__option-sublabel {
      font-size: 80%;
      color: var(--gray-600);
      margin-top: 0.15rem;
    }

    .multiselect__option--highlight .lazy-autocomplete__option-sublabel {
      color: inherit;
      opacity: 0.85;
    }

    .multiselect__option--disabled .lazy-autocomplete__option-sublabel {
      color: inherit;
    }

    .lazy-autocomplete__searching {
      padding: 0.75rem;
      font-size: 90%;
      color: var(--gray-600);
    }

    .lazy-autocomplete__more {
      padding: 0.5rem 0.75rem;
      font-size: 80%;
      color: var(--gray-600);
      border-top: 1px solid var(--gray-200);
    }

    .multiselect__content-wrapper {
      box-shadow: $box-shadow;
    }
  }
</style>
